import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Search, ArrowLeft, Rocket, Target, Ruler, SearchIcon, Lightbulb,
  Shield, CheckCircle, Zap, LayoutDashboard, HelpCircle, ChevronRight,
  BookOpen, Clock, Tag,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Rocket: Rocket, Target: Target, Ruler: Ruler, Search: SearchIcon,
  Lightbulb: Lightbulb, Shield: Shield, CheckCircle: CheckCircle,
  Zap: Zap, LayoutDashboard: LayoutDashboard, HelpCircle: HelpCircle,
};

export default function HelpCenter() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiAsking, setAiAsking] = useState(false);

  const selectedCategory = searchParams.get("cat");
  const selectedSlug = searchParams.get("article");

  useEffect(() => {
    Promise.all([
      fetch(`${API}/help/categories`).then(r => r.json()),
      fetch(`${API}/help/articles`).then(r => r.json()),
    ]).then(([catData, artData]) => {
      setCategories(catData.categories || []);
      setArticles(artData.articles || []);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (selectedSlug) {
      fetch(`${API}/help/articles/${selectedSlug}`)
        .then(r => r.json())
        .then(d => setSelectedArticle(d.article))
        .catch(() => setSelectedArticle(null));
    } else {
      setSelectedArticle(null);
    }
  }, [selectedSlug]);

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults(null); return; }
    const res = await fetch(`${API}/help/search?q=${encodeURIComponent(q)}`);
    const d = await res.json();
    setSearchResults(d.results || []);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery, doSearch]);

  const openArticle = (slug) => setSearchParams({ article: slug });
  const openCategory = (catId) => setSearchParams({ cat: catId });
  const goHome = () => setSearchParams({});

  const filteredArticles = selectedCategory
    ? articles.filter(a => a.category === selectedCategory)
    : articles;

  const askAi = async () => {
    if (!aiQuestion.trim()) return;
    setAiAsking(true);
    setAiAnswer("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/help/ask-ai`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question: aiQuestion }),
      });
      const d = await res.json();
      setAiAnswer(d.answer || "Sorry, I couldn't find an answer.");
    } catch {
      setAiAnswer("Unable to reach AI assistant. Please try again.");
    }
    setAiAsking(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="help-center-page">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="help-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="h-5 w-px bg-zinc-700" />
            <button onClick={goHome} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <BookOpen className="w-5 h-5 text-emerald-400" />
              <span className="font-semibold text-lg tracking-tight">Help Center</span>
            </button>
          </div>
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search help articles..."
              className="pl-10 bg-zinc-900 border-zinc-700 text-sm"
              data-testid="help-search-input"
            />
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Search Results */}
        {searchResults !== null ? (
          <div data-testid="help-search-results">
            <div className="flex items-center gap-2 mb-6">
              <SearchIcon className="w-5 h-5 text-zinc-400" />
              <h2 className="text-lg font-medium">
                {searchResults.length} result{searchResults.length !== 1 && "s"} for "{searchQuery}"
              </h2>
              <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(""); setSearchResults(null); }}>
                Clear
              </Button>
            </div>
            <div className="space-y-3">
              {searchResults.map(a => (
                <button
                  key={a.slug}
                  onClick={() => openArticle(a.slug)}
                  className="w-full text-left p-4 rounded-lg bg-zinc-900/60 border border-zinc-800 hover:border-zinc-600 transition-all group"
                >
                  <h3 className="font-medium group-hover:text-emerald-400 transition-colors">{a.title}</h3>
                  <p className="text-sm text-zinc-400 mt-1">{a.summary}</p>
                  <div className="flex gap-2 mt-2">
                    {a.tags?.slice(0, 3).map(t => (
                      <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400">{t}</span>
                    ))}
                  </div>
                </button>
              ))}
              {searchResults.length === 0 && (
                <p className="text-zinc-500 text-center py-12">No articles found. Try a different search term or ask AI below.</p>
              )}
            </div>
          </div>
        ) : selectedArticle ? (
          /* Article View */
          <div data-testid="help-article-view">
            <button onClick={goHome} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-200 mb-6 transition-colors">
              <ArrowLeft className="w-3 h-3" /> Back to all topics
            </button>
            <article className="max-w-3xl">
              <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
                <Tag className="w-3 h-3" />
                <span className="capitalize">{selectedArticle.category?.replace("dmaic-", "").replace("-", " ")}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-4 tracking-tight">{selectedArticle.title}</h1>
              <p className="text-zinc-400 text-base mb-8 leading-relaxed">{selectedArticle.summary}</p>
              <div className="prose prose-invert prose-emerald prose-sm max-w-none
                prose-headings:font-semibold prose-headings:tracking-tight
                prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-zinc-100
                prose-p:text-zinc-300 prose-p:leading-relaxed
                prose-li:text-zinc-300
                prose-strong:text-zinc-100
                prose-table:text-sm prose-table:border prose-table:border-zinc-700
                prose-th:text-left prose-th:p-2.5 prose-th:bg-zinc-800 prose-th:border prose-th:border-zinc-700 prose-th:font-semibold prose-th:text-zinc-200
                prose-td:p-2.5 prose-td:border prose-td:border-zinc-800
                prose-code:text-emerald-400 prose-code:bg-zinc-900 prose-code:px-1 prose-code:rounded
              ">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{selectedArticle.content}</ReactMarkdown>
              </div>
            </article>
          </div>
        ) : (
          /* Category / Home View */
          <div data-testid="help-home-view">
            {!selectedCategory && (
              <>
                <div className="text-center mb-12">
                  <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">How can we help?</h1>
                  <p className="text-zinc-400 max-w-lg mx-auto">Browse articles by topic, search for answers, or ask our AI assistant.</p>
                </div>
                {/* Category Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-12" data-testid="help-category-grid">
                  {categories.map(cat => {
                    const Icon = ICON_MAP[cat.icon] || HelpCircle;
                    const count = articles.filter(a => a.category === cat.id).length;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => openCategory(cat.id)}
                        className="flex flex-col items-center gap-2 p-4 rounded-lg bg-zinc-900/60 border border-zinc-800 hover:border-emerald-600/40 hover:bg-zinc-900 transition-all group"
                        data-testid={`help-cat-${cat.id}`}
                      >
                        <Icon className="w-6 h-6 text-zinc-400 group-hover:text-emerald-400 transition-colors" />
                        <span className="text-sm font-medium text-center leading-tight">{cat.label}</span>
                        <span className="text-xs text-zinc-500">{count} article{count !== 1 && "s"}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {selectedCategory && (
              <div>
                <button onClick={goHome} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-200 mb-4 transition-colors">
                  <ArrowLeft className="w-3 h-3" /> All topics
                </button>
                <h2 className="text-xl font-bold mb-6">
                  {categories.find(c => c.id === selectedCategory)?.label}
                </h2>
              </div>
            )}

            {/* Article List */}
            <div className="space-y-3" data-testid="help-article-list">
              {filteredArticles.map(a => (
                <button
                  key={a.slug}
                  onClick={() => openArticle(a.slug)}
                  className="w-full text-left p-4 rounded-lg bg-zinc-900/40 border border-zinc-800/60 hover:border-zinc-600 hover:bg-zinc-900/80 transition-all group flex items-center justify-between"
                  data-testid={`help-article-${a.slug}`}
                >
                  <div>
                    <h3 className="font-medium group-hover:text-emerald-400 transition-colors">{a.title}</h3>
                    <p className="text-sm text-zinc-500 mt-0.5">{a.summary}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 flex-shrink-0 ml-4 transition-colors" />
                </button>
              ))}
            </div>

            {/* Ask AI Section */}
            <div className="mt-16 max-w-2xl mx-auto" data-testid="help-ask-ai-section">
              <div className="p-6 rounded-xl bg-zinc-900/60 border border-zinc-800">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-5 h-5 text-amber-400" />
                  <h3 className="font-semibold text-base">Still confused? Ask AI</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-900/40 text-emerald-400 border border-emerald-800/40">Free</span>
                </div>
                <p className="text-sm text-zinc-400 mb-4">Get instant answers about BlackBelt AI, DMAIC methodology, or your specific situation.</p>
                <div className="flex gap-2">
                  <Input
                    value={aiQuestion}
                    onChange={e => setAiQuestion(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && askAi()}
                    placeholder="e.g. When should I use DOE vs regression?"
                    className="bg-zinc-800 border-zinc-700 text-sm flex-1"
                    data-testid="help-ask-ai-input"
                  />
                  <Button onClick={askAi} disabled={aiAsking || !aiQuestion.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="help-ask-ai-btn">
                    {aiAsking ? "Thinking..." : "Ask"}
                  </Button>
                </div>
                {aiAnswer && (
                  <div className="mt-4 p-4 rounded-lg bg-zinc-800/60 border border-zinc-700" data-testid="help-ai-answer">
                    <div className="prose prose-invert prose-sm max-w-none prose-p:text-zinc-300 prose-headings:text-zinc-100">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiAnswer}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
